import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { AlertTriangle, ArrowDownToDot, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  rainfallGullyTaggingAtom,
  rainfallStormScaleAtom,
} from '@/atoms/rainfall.atom';
import { Switch } from '@/components/ui/switch';
import { execute } from '@/graphql/execute';
import {
  scaledFlowLineRunoffLs,
  scaledPondMetrics,
} from '@/lib/rainfallScaling';
import { isPondDrained } from '@/lib/rainfallStyling';
import { updateFeatureMutation } from '@/queries/feature.query';

/**
 * Renders rainfall-feature-specific stats (pond / flow line / watershed)
 * inside the standard `FeatureProperties` drawer instead of the
 * separate floating inspector. Drives off the feature's `properties`
 * directly — same field names the worker emits on `rainfall.geojson`.
 *
 * `parseRainfallFeature` returns null for non-rainfall features so the
 * caller can branch on a single check (mirrors the stockpile pattern
 * in FeatureProperties.tsx).
 */

export type RainfallKind = 'pond' | 'flowLine' | 'watershed';

export interface RainfallFeatureProperties {
  kind: RainfallKind;
  properties: Record<string, unknown>;
}

/** Detect a rainfall worker-emitted feature. Returns null for everything else. */
export function parseRainfallFeature(
  raw: Record<string, unknown> | null | undefined,
): RainfallFeatureProperties | null {
  if (!raw) return null;
  const kind = raw.kind;
  if (kind === 'pond' || kind === 'flowLine' || kind === 'watershed') {
    return { kind, properties: raw };
  }
  return null;
}

interface Props {
  parsed: RainfallFeatureProperties;
  /** Storm duration in hours, plumbed from the parent layer's
   *  `metadata.rainfallSummary.storm.durationH`. Used for the
   *  "overflows mid-storm" warning under capacity. Undefined for
   *  TOPOLOGY-mode runs (in which case storm-derived rows hide). */
  stormDurationH?: number;
  /** Feature id + layerId so the gully toggle can persist via
   *  `updateFeature`. Both are present on every selected feature in
   *  the right-side drawer; falling back to optimistic-only if either
   *  is missing keeps the UI working in degraded states. */
  featureId?: string;
  featureLayerId?: string;
}

export function RainfallFeatureView({ parsed, stormDurationH, featureId, featureLayerId }: Props) {
  const { t } = useTranslation('project');
  const { kind, properties } = parsed;
  const [gullies, setGullies] = useAtom(rainfallGullyTaggingAtom);
  const stormScale = useAtomValue(rainfallStormScaleAtom);
  const queryClient = useQueryClient();

  const num = (key: string): number | undefined => {
    const v = properties[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  };
  const bool = (key: string): boolean => properties[key] === true;

  // Pond-only derived values for capacity flag + spillover.
  // Storm-derived metrics flow through the central `scaledPondMetrics`
  // helper so the inspector, leaderboard, fill animations, and CSV
  // export agree on what "scale = 1.5" means.
  const pondId = typeof properties.pondId === 'string' ? properties.pondId : undefined;
  const drained = isPondDrained(properties, gullies);
  const scaled = scaledPondMetrics(properties, stormScale);
  const isScaled = stormScale !== 1.0;
  const stormDurationS = stormDurationH != null ? stormDurationH * 3600 : undefined;

  // Gully heuristic nudge — small + shallow ponds are usually road
  // gullies in disguise. We don't auto-set, just hint.
  const surfaceAreaM2 = num('surfaceAreaM2');
  const maxDepthM = num('maxDepthM');
  const looksLikeGully =
    !drained &&
    surfaceAreaM2 != null &&
    surfaceAreaM2 < 1 &&
    maxDepthM != null &&
    maxDepthM < 0.05;

  return (
    <div className='space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm dark:border-stone-700 dark:bg-stone-800/40'>
      <div className='font-medium'>{t(`rainfallAnalysis.inspect.${kind}.label`)}</div>

      {kind === 'pond' && (
        <dl className='space-y-1 text-xs'>
          <Row label={t('rainfallAnalysis.inspect.pond.maxDepth')} value={fmtMeters(num('maxDepthM'))} />
          <Row
            label={t('rainfallAnalysis.inspect.pond.surfaceArea')}
            value={fmtSqMeters(num('surfaceAreaM2'))}
          />
          <Row label={t('rainfallAnalysis.inspect.pond.volume')} value={fmtCubicMeters(num('volumeM3'))} />
          <Row
            label={t('rainfallAnalysis.inspect.pond.catchment')}
            value={fmtSqMeters(num('catchmentAreaM2'))}
          />
          {/* Storm-mode fields — only render when the worker actually
              wrote them (null in TOPOLOGY runs). Values flow through
              `scaledPondMetrics` so they react to the what-if slider;
              when scale ≠ 1× we mark them with a leading `~` so the
              audience sees these are derived from the scrubber, not
              the original run. */}
          {scaled.inflowLs != null && (
            <Row
              label={t('rainfallAnalysis.inspect.pond.inflow')}
              value={fmtFlowLs(scaled.inflowLs)}
              scaled={isScaled}
            />
          )}
          {scaled.fillTimeS != null && (
            <Row
              label={t('rainfallAnalysis.inspect.pond.fillTime')}
              value={fmtDurationS(scaled.fillTimeS)}
              scaled={isScaled}
            />
          )}
          {scaled.totalRunoffM3 != null && (
            <Row
              label={t('rainfallAnalysis.inspect.pond.runoffVolume')}
              value={fmtCubicMeters(scaled.totalRunoffM3)}
              scaled={isScaled}
            />
          )}

          {/* Capacity flag + spillover. Only rendered for storm-mode
              ponds (totalRunoffM3 != null) — TOPOLOGY runs don't have
              the runoff number to compare against. */}
          {scaled.totalRunoffM3 != null && (
            <CapacityBlock
              drained={drained}
              captureRatio={scaled.captureRatio ?? undefined}
              spilloverM3={scaled.spilloverM3 ?? undefined}
              fillTimeS={scaled.fillTimeS ?? undefined}
              stormDurationS={stormDurationS}
              isScaled={isScaled}
            />
          )}

          {/* Chain context. Always present in v2 worker output. */}
          {properties.terminal === true ? (
            <div className='mt-1 rounded bg-sky-50 px-2 py-1 text-[11px] leading-snug text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'>
              {t('rainfallAnalysis.inspect.pond.terminal')}
            </div>
          ) : (
            num('chainOrder') != null && (
              <Row
                label={t('rainfallAnalysis.inspect.pond.chainPosition')}
                value={t('rainfallAnalysis.inspect.pond.chainPositionValue', {
                  order: num('chainOrder'),
                })}
              />
            )
          )}
          {bool('edgeTouching') && (
            <Warning>{t('rainfallAnalysis.inspect.pond.edgeTouchingWarning')}</Warning>
          )}

          {/* Gully tagging toggle — sits at the bottom of the pond
              block so it doesn't pull focus from the headline stats.
              Heuristic nudge appears just above when the pond's tiny
              dimensions match a typical road gully. */}
          {pondId != null && (
            <div className='mt-2 border-t border-slate-200 pt-2 dark:border-stone-700'>
              {looksLikeGully && (
                <div className='mb-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] leading-snug text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'>
                  {t('rainfallAnalysis.inspect.pond.gullyHint')}
                </div>
              )}
              <label className='flex cursor-pointer items-center justify-between gap-2 rounded-md py-0.5'>
                <span className='flex flex-col gap-0.5'>
                  <span className='text-xs font-medium'>
                    {t('rainfallAnalysis.inspect.pond.gullyToggleLabel')}
                  </span>
                  <span className='text-[10px] text-slate-500 dark:text-stone-400'>
                    {t('rainfallAnalysis.inspect.pond.gullyToggleHelp')}
                  </span>
                </span>
                <Switch
                  checked={drained}
                  onCheckedChange={async (next) => {
                    // Optimistic UI: update the override atom immediately so
                    // every reader (capacity chip, leaderboard, fill animation,
                    // arrows) flips before the server round-trip lands.
                    setGullies((prev) => ({ ...prev, [pondId]: next }));
                    // Persist to feature.properties.hasGully via updateFeature
                    // so the tag survives reload + travels with the layer.
                    // If we don't have an id (e.g. unsaved feature), the atom
                    // override is the only state we can keep — graceful
                    // degrade to session-only behaviour.
                    if (!featureId || !featureLayerId) return;
                    try {
                      const nextProps = { ...properties, hasGully: next };
                      await execute(updateFeatureMutation, {
                        input: { id: featureId, properties: nextProps },
                      });
                      // Refresh the layer's features so other consumers
                      // (leaderboard, map fill, arrows) read the new
                      // hasGully from cache; the override atom already
                      // shows the correct value, so the cache update is
                      // a no-op visually.
                      queryClient.invalidateQueries({
                        queryKey: ['features', featureLayerId],
                      });
                    } catch (err) {
                      // Roll back the override on failure so the UI
                      // matches the (still-stale) server state.
                      setGullies((prev) => {
                        const copy = { ...prev };
                        delete copy[pondId];
                        return copy;
                      });
                      console.error('Failed to persist gully toggle', err);
                      toast.error(t('rainfallAnalysis.inspect.pond.gullySaveFailed'));
                    }
                  }}
                />
              </label>
            </div>
          )}
        </dl>
      )}

      {kind === 'flowLine' && (
        <dl className='space-y-1 text-xs'>
          <Row
            label={t('rainfallAnalysis.inspect.flowLine.upstreamArea')}
            value={fmtSqMeters(num('flowAccumulationM2'))}
          />
          <Row label={t('rainfallAnalysis.inspect.flowLine.length')} value={fmtMeters(num('lengthM'))} />
          <Row
            label={t('rainfallAnalysis.inspect.flowLine.meanSlope')}
            value={fmtPercent(num('meanSlope'))}
          />
          {num('peakRunoffLs') != null && (
            <Row
              label={t('rainfallAnalysis.inspect.flowLine.peakRunoff')}
              value={fmtFlowLs(
                scaledFlowLineRunoffLs(num('peakRunoffLs'), stormScale) ?? undefined,
              )}
              scaled={isScaled}
            />
          )}
          {bool('exitsSurvey') && (
            <Warning>{t('rainfallAnalysis.inspect.flowLine.exitsSurveyWarning')}</Warning>
          )}
        </dl>
      )}

      {kind === 'watershed' && (
        <dl className='space-y-1 text-xs'>
          <Row label={t('rainfallAnalysis.inspect.watershed.area')} value={fmtSqMeters(num('areaM2'))} />
          {bool('edgeTouching') && (
            <Warning>{t('rainfallAnalysis.inspect.watershed.edgeTouchingWarning')}</Warning>
          )}
        </dl>
      )}
    </div>
  );
}

function CapacityBlock({
  drained,
  captureRatio,
  spilloverM3,
  fillTimeS,
  stormDurationS,
  isScaled,
}: {
  drained: boolean;
  captureRatio?: number;
  spilloverM3?: number;
  fillTimeS?: number;
  stormDurationS?: number;
  /** True when the what-if slider is anything but 1×. Drives the `~`
   *  scaled-prefix on the spillover row so the audience sees that
   *  the value is derived from the scrubber. */
  isScaled?: boolean;
}) {
  const { t } = useTranslation('project');

  // Tooltip surfaces the engineering definition behind the chip — drainage
  // engineers expect to see the formula, not just a coloured pill. Same
  // string for every variant; the chip's pct/colour communicates the rest.
  const capacityTooltip = t('rainfallAnalysis.inspect.pond.capacity.formula') as string;

  if (drained) {
    return (
      <div
        title={t('rainfallAnalysis.inspect.pond.capacity.drainedTooltip') as string}
        className='mt-1 inline-flex items-center gap-1.5 rounded bg-slate-100 px-2 py-1 text-[11px] font-medium leading-snug text-slate-700 dark:bg-stone-700/40 dark:text-stone-200'>
        <ArrowDownToDot className='size-3.5' />
        {t('rainfallAnalysis.inspect.pond.capacity.drained')}
      </div>
    );
  }
  if (captureRatio == null) return null;

  const pct = Math.round(captureRatio * 100);
  let chipClass: string;
  let label: string;
  let Icon: typeof CheckCircle2;
  if (captureRatio >= 1) {
    chipClass =
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    label = t('rainfallAnalysis.inspect.pond.capacity.green', { pct });
    Icon = CheckCircle2;
  } else if (captureRatio >= 0.5) {
    chipClass =
      'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    label = t('rainfallAnalysis.inspect.pond.capacity.amber', { pct });
    Icon = AlertTriangle;
  } else {
    chipClass = 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300';
    label = t('rainfallAnalysis.inspect.pond.capacity.red', { pct });
    Icon = XCircle;
  }

  // "Overflows mid-storm" warning is the headline civil-engineering
  // signal — show it even when the pond's capture ratio looks fine,
  // because a pond can be ≥1.0 by total volume yet still overtop
  // partway through a peak storm interval.
  const overflowsMidStorm =
    fillTimeS != null &&
    Number.isFinite(fillTimeS) &&
    fillTimeS > 0 &&
    stormDurationS != null &&
    stormDurationS > 0 &&
    fillTimeS < stormDurationS;

  return (
    <div className='mt-1 space-y-1'>
      <div
        title={capacityTooltip}
        className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium leading-snug ${chipClass}`}>
        <Icon className='size-3.5' />
        {label}
      </div>
      {spilloverM3 != null && spilloverM3 > 0 && (
        <Row
          label={t('rainfallAnalysis.inspect.pond.spillover')}
          value={fmtCubicMeters(spilloverM3)}
          scaled={isScaled}
        />
      )}
      {overflowsMidStorm && (
        <div className='inline-flex items-center gap-1.5 rounded bg-rose-50 px-2 py-1 text-[11px] font-medium leading-snug text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'>
          <AlertTriangle className='size-3.5' />
          {t('rainfallAnalysis.inspect.pond.capacity.overflowsMidStormAt', {
            time: fmtDurationS(fillTimeS),
          })}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  scaled,
}: {
  label: string;
  value: string;
  /** When true, prefixes the value with a tilde + italic to mark it as
   *  derived from the what-if scrubber rather than the original run. */
  scaled?: boolean;
}) {
  return (
    <div className='flex justify-between gap-2'>
      <dt className='text-slate-500 dark:text-stone-400'>{label}</dt>
      <dd
        className={`font-mono tabular-nums text-slate-900 dark:text-stone-100 ${
          scaled ? 'italic text-sky-700 dark:text-sky-300' : ''
        }`}>
        {scaled ? `~${value}` : value}
      </dd>
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className='mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] leading-snug text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'>
      {children}
    </div>
  );
}

const fmtMeters = (v: number | undefined) =>
  v == null ? '—' : v >= 1 ? `${v.toFixed(2)} m` : `${(v * 100).toFixed(1)} cm`;
const fmtSqMeters = (v: number | undefined) =>
  v == null ? '—' : v >= 10_000 ? `${(v / 10_000).toFixed(2)} ha` : `${v.toFixed(1)} m²`;
const fmtCubicMeters = (v: number | undefined) => {
  if (v == null) return '—';
  // Tiny ponds and small spillovers read more naturally in litres than
  // as "0.04 m³" — civil-engineering convention is L for sub-cubic-metre
  // volumes, m³ above that. 1 m³ = 1000 L. Below 1 L drop to 0.x L so
  // truly empty values still show.
  const absV = Math.abs(v);
  if (absV < 0.001) return `${(v * 1000).toFixed(2)} L`;
  if (absV < 1) return `${(v * 1000).toFixed(0)} L`;
  return `${v.toFixed(2)} m³`;
};
const fmtPercent = (v: number | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)} %`);
const fmtFlowLs = (v: number | undefined) => {
  if (v == null) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)} m³/s`;
  if (v >= 1) return `${v.toFixed(1)} L/s`;
  return `${v.toFixed(2)} L/s`;
};
export const fmtDurationS = (v: number | undefined) => {
  if (v == null) return '—';
  if (v < 60) return `${v.toFixed(0)} s`;
  if (v < 3600) return `${(v / 60).toFixed(1)} min`;
  if (v < 86_400) return `${(v / 3600).toFixed(1)} h`;
  return `${(v / 86_400).toFixed(1)} d`;
};
